import {AfterViewInit, Component, ElementRef, OnInit, QueryList, ViewChildren} from '@angular/core';
import {WorkbenchCommandRegistry} from "../../../services/workbench-command-registry.service";
import {
    EditingScreenshotEvent,
    InspectorEvent,
    InspectorEventType,
    InspectorImplementation,
    OpenScreenshotEvent
} from '../../../types';
import {
    AuditActivityId,
    LocalScreenshotId,
    ProjectId,
    ScreenshotId,
    TestRunId
} from '@webmate-web-core/shared/types/ids';
import {WorkbenchContextService} from '../../../services/workbench-context.service';
import {delay, finalize, mergeMap, switchMap, take} from 'rxjs/operators';
import {WorkbenchTestService} from '../../../services/workbench-test.service';
import {ImageService} from '@webmate-web-core/shared/services/image.service';
import {AddToFindingEvent} from '../inspector-shared-tools/add-to-finding.component';
import {ImageMetadata} from '@webmate-web-core/shared/api/rest/image.restapi';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Observable, of} from 'rxjs';
import {SearchService} from '@webmate-web-core/shared/services/search.service';
import {ModalToolContainerService} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {faAdjust} from '@fortawesome/pro-solid-svg-icons';
import {
    ContrastAnalysisViewComponent,
    ContrastAnalysisViewComponentConfig
} from '@webmate-web-core/shared/tools/contrast-analysis-view/contrast-analysis-view.component';
import {ContrastAnalysisService} from '@webmate-web-core/shared/services/contrast-analysis.service';

@UntilDestroy()
@Component({
    selector: 'wm-screenshot-inspector',
    templateUrl: './screenshot-inspector.component.html',
    styleUrls: ['./screenshot-inspector.component.less']
})
export class ScreenshotInspectorComponent implements InspectorImplementation, OnInit, AfterViewInit {

    @ViewChildren('nameInput') nameInput: QueryList<ElementRef>;

    projectId: ProjectId;
    localId: LocalScreenshotId;
    id: ScreenshotId;
    name?: string;
    metadata: ImageMetadata;
    inputName: string = undefined;
    modifiedName: string;
    addedToFinding: boolean = false;
    modifyingName: boolean;
    mimeType: string;

    activeTestRun?: TestRunId;
    activeActivity?: AuditActivityId;
    isEditingScreenshot: boolean = false;

    analyzingContrast: boolean = false;
    contrastAnalysis: any;

    constructor(private readonly workbenchCommandRegistry: WorkbenchCommandRegistry,
                private readonly modalToolService: ModalToolContainerService,
                private contextService: WorkbenchContextService,
                private workbenchTestService: WorkbenchTestService,
                private imageService: ImageService,
                private searchService: SearchService,
                private contrastAnalysisService: ContrastAnalysisService,
    ){}

    ngOnInit() {
        this.projectId = this.contextService.getProjectId();
        this.contextService.subscribeWorkbenchContext$().subscribe(context => {
            this.activeTestRun = context?.testRunContext?.activeTestRun;
            this.activeActivity = context?.auditContext?.activeAuditActivity?.id;
        });
    }

    ngAfterViewInit() {
        this.nameInput.changes.subscribe(changes => {
            // this is needed to set the focus automatically after the resultInput is rendered
            this.nameInput.first?.nativeElement.focus();
        });
    }

    handleInspectorEvent(event: InspectorEvent): void {
        switch (event.type) {
            case 'OpenScreenshot': {
                let openScreenshotEvent: OpenScreenshotEvent = event as OpenScreenshotEvent;
                this.localId = openScreenshotEvent.localId;
                this.id = openScreenshotEvent.id;
                this.isEditingScreenshot = openScreenshotEvent.inEditMode;
                if (this.id) {
                    this.getMetadata().subscribe(metadata => {
                        this.name = metadata.name;
                        this.metadata = metadata;
                    });
                    this.searchService.getIndexingNotifications(this.projectId).pipe(
                        untilDestroyed(this),
                        delay(200),
                        switchMap(() => {
                            return this.getMetadata();
                        })
                    ).subscribe(metadata => {
                        this.name = metadata.name;
                        this.metadata = metadata;
                    });
                }
                let downloadUrl$: Observable<string>;
                if (this.localId) {
                    downloadUrl$ = this.imageService.getLocalScreenshot(this.localId);
                } else if (this.id) {
                    downloadUrl$ = of(this.imageService.getImageUrl(this.id, undefined, true, this.name));
                }
                if (downloadUrl$) {
                    downloadUrl$.subscribe(async (url: string) => {
                        const response = await fetch(url);
                        this.mimeType = response.headers.get('Content-Type');
                    });
                }
                return;
            }
            case 'EditingScreenshot': {
                let editingScreenshotEvent: EditingScreenshotEvent = event as EditingScreenshotEvent;
                this.isEditingScreenshot = editingScreenshotEvent.editing;
                return;
            }
        }
    }

    getMetadata() {
        return this.imageService.getImageMetadata(this.id);
    }

    downloadScreenshot(){
        let downloadName: string = this.name;
        let downloadUrl = null;
        if (this.inputName) {
            downloadName = this.inputName;
        }
        if (this.localId) {
            this.imageService.getLocalScreenshot(this.localId).subscribe(url => {
                downloadUrl = url;
                this.workbenchCommandRegistry.executeCommand("workbench-tools:downloadScreenshot", {localId: this.localId, imageUrl: downloadUrl, name: downloadName});
                this.inputName = undefined;
            });
        } else {
            this.workbenchCommandRegistry.executeCommand("workbench-tools:downloadScreenshot", {id: this.id, name: downloadName});
            this.inputName = undefined;
        }
    }

    addToFinding(event: AddToFindingEvent) {
        if (this.id) {
            this.workbenchTestService.addScreenshotToFinding(event.findingId, this.id as ScreenshotId, event.optDescription).pipe(take(1)).subscribe(() =>{
                this.addedToFinding = true;
                setTimeout(()=> this.addedToFinding = false, 5000);
            })
        } else {
            this.imageService.persistLocalScreenshot(this.localId, 'New Screenshot', this.contextService.getProjectId()).pipe(mergeMap(id => {
                return this.workbenchTestService.addScreenshotToFinding(event.findingId, id as ScreenshotId, event.optDescription);
            }), take(1)).subscribe(() => {
                this.addedToFinding = true;
                setTimeout(()=> this.addedToFinding = false, 5000);
            });
        }
    }

    changeName() {
        this.imageService.setImageName(this.id, this.modifiedName).subscribe(() => {
            this.name = this.modifiedName;
            this.modifyingName = false;
        });
    }

    analyzeContrast() {
        if (!this.analyzingContrast && (this.id || this.localId)) {
            this.analyzingContrast = true;

            const imageUrl$ = this.id
                ? of(this.imageService.getImageUrl(this.id))
                : this.imageService.getLocalScreenshot(this.localId);

            this.contrastAnalysisService.analyzeContrast$(this.projectId, this.id, imageUrl$).pipe(
                finalize(() => this.analyzingContrast = false),
                take(1)
            ).subscribe(({ analysis, imageUrl }) => {
                this.contrastAnalysis = analysis;
                this.modalToolService.openTool(
                    ContrastAnalysisViewComponent,
                    new ContrastAnalysisViewComponentConfig(imageUrl, this.contrastAnalysis)
                );
            });
        } else {
            throw new Error("Preconditions not met for contrast analysis");
        }
    }

    protected readonly faAdjust = faAdjust;
}
